import {
  GlobeAltIcon,
  CubeIcon,
  CubeTransparentIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import React, { useRef } from 'react';

export type C4ViewType = 'systemContext' | 'container' | 'component';

interface ViewInfo {
  id: C4ViewType;
  name: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const VIEW_OPTIONS: ViewInfo[] = [
  {
    id: 'systemContext',
    name: 'System Context',
    description: 'C4 Level 1 - System Context diagram',
    icon: GlobeAltIcon,
  },
  {
    id: 'container',
    name: 'Container',
    description: 'C4 Level 2 - Container diagram',
    icon: CubeIcon,
  },
  {
    id: 'component',
    name: 'Component',
    description: 'C4 Level 3 - Component diagram',
    icon: CubeTransparentIcon,
  },
];

interface ViewSelectorProps {
  /**
   * Currently selected view type
   */
  selectedView: C4ViewType;
  /**
   * Callback when view selection changes
   */
  onViewChange: (_view: C4ViewType) => void;
  /**
   * Available views detected from the DSL
   */
  availableViews?: C4ViewType[];
  /**
   * Whether selector is disabled
   */
  disabled?: boolean;
}

/**
 * ViewSelector component for switching between C4 diagram view types
 * @param props - Component props
 * @return JSX element for the view selector
 */
export const ViewSelector: React.FC<ViewSelectorProps> = ({
  selectedView,
  onViewChange,
  availableViews = ['systemContext', 'container', 'component'],
  disabled = false,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = VIEW_OPTIONS.filter((option) =>
    availableViews.includes(option.id),
  );

  return (
    <div ref={dropdownRef} className="relative">
      {/* View selector using native select for accessibility */}
      <select
        value={selectedView}
        onChange={(e) => onViewChange(e.target.value as C4ViewType)}
        disabled={disabled}
        className={`
          appearance-none flex items-center gap-2 px-4 py-2 pr-10 rounded-lg text-sm font-medium
          transition-colors border cursor-pointer
          ${
    disabled
      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
    }
        `}
      >
        {filteredOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-700"
      />
    </div>
  );
};

/**
 * Parse DSL content to detect available view types
 * @param dslContent - Structurizr DSL content
 * @returns Array of available view types
 */
export const detectAvailableViews = (dslContent: string): C4ViewType[] => {
  const views: C4ViewType[] = [];

  // Extract the views section - find 'views {' and match until balanced braces
  const viewsStartRegex = /views\s*\{/i;
  const viewsStartMatch = viewsStartRegex.exec(dslContent);
  if (viewsStartMatch?.index === undefined) {
    return views;
  }

  // Get content after 'views {'
  const startIdx = viewsStartMatch.index + viewsStartMatch[0].length;
  let braceCount = 1;
  let endIdx = startIdx;

  for (let i = startIdx; i < dslContent.length && braceCount > 0; i++) {
    if (dslContent[i] === '{') {braceCount++;}
    else if (dslContent[i] === '}') {braceCount--;}
    endIdx = i;
  }

  const viewsContent = dslContent.substring(startIdx, endIdx).toLowerCase();

  // Check for each view type declaration in views section
  // Pattern: viewType identifier "key" or viewType identifier key
  if (/systemcontext\s+\w+/i.test(viewsContent)) {
    views.push('systemContext');
  }
  if (/container\s+\w+/i.test(viewsContent)) {
    views.push('container');
  }
  if (/component\s+\w+/i.test(viewsContent)) {
    views.push('component');
  }

  return views;
};

/**
 * Extract view key from DSL for a specific view type
 * Used to tell Kroki which view to render
 * @param dslContent - Structurizr DSL content
 * @param viewType - The view type to find
 * @returns The view key if found, or undefined
 */
export const extractViewKey = (
  dslContent: string,
  viewType: C4ViewType,
): string | undefined => {
  const viewTypeKeyword =
    viewType === 'systemContext' ? 'systemContext' : viewType;

  // Match view definition: systemContext identifier "key" or container identifier "key"
  const pattern = new RegExp(
    `${viewTypeKeyword}\\s+\\w+\\s+"([^"]+)"`,
    'i',
  );
  const match = pattern.exec(dslContent);

  return match?.[1];
};
